(function () {
  const CLAIMIFY_LANGUAGES = new Set(['en', 'ar', 'fr', 'de', 'es', 'it', 'pt', 'nl', 'ja', 'zh']);
  const CLAIMIFY_SCRIPT_MATCH = 'claimifyPreact';
  const MAX_APPLY_ATTEMPTS = 10;
  const APPLY_RETRY_MS = 500;
  const WAIT_FOR_SELECT_MS = 30000;
  const POLL_MS = 200;
  const WATCHED_BLOCKS = new WeakSet();

  const selectValueSetter = Object.getOwnPropertyDescriptor(
    HTMLSelectElement.prototype,
    'value'
  )?.set;

  const getShopLanguageBase = (shopLanguage) => {
    return (shopLanguage || document.documentElement.lang || '').toLowerCase().split('-')[0];
  };

  const shouldSyncLanguage = (shopLanguage) => {
    return getShopLanguageBase(shopLanguage) !== 'pl';
  };

  const resolveTargetLanguage = (shopLanguage, availableValues = []) => {
    const base = getShopLanguageBase(shopLanguage) || 'en';

    if (availableValues.length > 0) {
      if (base && availableValues.includes(base)) return base;
      if (availableValues.includes('en')) return 'en';
      return availableValues[0];
    }

    if (CLAIMIFY_LANGUAGES.has(base)) return base;
    return 'en';
  };

  const getClaimifyRoot = (appBlock) => {
    return appBlock.querySelector('#claimify-preact-root');
  };

  const getClaimifyShadowRoot = (appBlock) => {
    return getClaimifyRoot(appBlock)?.shadowRoot || null;
  };

  const patchClaimifySettings = (appBlock, shopLanguage) => {
    const root = getClaimifyRoot(appBlock);
    if (!root || root.dataset.appBlockLangPatched === 'true') {
      return resolveTargetLanguage(shopLanguage);
    }

    const settingsRaw = root.getAttribute('data-settings');
    if (!settingsRaw) {
      return resolveTargetLanguage(shopLanguage);
    }

    let settings;
    try {
      settings = JSON.parse(settingsRaw);
    } catch {
      return resolveTargetLanguage(shopLanguage);
    }

    const targetLanguage = resolveTargetLanguage(shopLanguage);
    settings.language_source = 'shopify';
    settings.language = targetLanguage;
    settings.default_language = targetLanguage;
    settings.initial_language = targetLanguage;
    settings.locale = targetLanguage;

    root.setAttribute('data-settings', JSON.stringify(settings));
    root.dataset.shopLanguage = targetLanguage;
    root.dataset.appBlockLangPatched = 'true';

    return targetLanguage;
  };

  const isLanguageSelect = (select) => {
    if (!(select instanceof HTMLSelectElement)) return false;

    const values = [...select.options]
      .map((option) => option.value)
      .filter((value) => /^[a-z]{2}$/i.test(value));

    if (values.length < 2) return false;

    return values.some((value) => CLAIMIFY_LANGUAGES.has(value));
  };

  const findLanguageSelect = (appBlock) => {
    const scopes = [appBlock];
    const shadow = getClaimifyShadowRoot(appBlock);

    if (shadow) {
      scopes.unshift(shadow);
    }

    for (const scope of scopes) {
      const select = [...scope.querySelectorAll('select')].find(isLanguageSelect);
      if (select) return select;
    }

    return null;
  };

  const applyLanguageToSelect = (select, language) => {
    const optionIndex = [...select.options].findIndex((option) => option.value === language);
    if (optionIndex < 0) return false;

    const option = select.options[optionIndex];
    option.selected = true;
    select.selectedIndex = optionIndex;

    if (selectValueSetter) {
      selectValueSetter.call(select, language);
    } else {
      select.value = language;
    }

    select.dispatchEvent(new Event('input', { bubbles: true, cancelable: true }));
    select.dispatchEvent(new Event('change', { bubbles: true, cancelable: true }));

    return select.value === language;
  };

  const applyLanguageWithRetries = (shopLanguage, select) => {
    let attempt = 0;
    let stopped = false;
    let retryId = null;
    let observer = null;

    const cleanup = () => {
      if (stopped) return;
      stopped = true;

      if (retryId) window.clearTimeout(retryId);
      if (observer) observer.disconnect();
    };

    const tryApply = () => {
      if (stopped) return;

      attempt += 1;
      const availableValues = [...select.options].map((option) => option.value);
      const targetLanguage = resolveTargetLanguage(shopLanguage, availableValues);

      if (select.value === targetLanguage) {
        cleanup();
        return;
      }

      if (applyLanguageToSelect(select, targetLanguage)) {
        cleanup();
        return;
      }

      if (attempt >= MAX_APPLY_ATTEMPTS) {
        cleanup();
        return;
      }

      retryId = window.setTimeout(tryApply, APPLY_RETRY_MS);
    };

    observer = new MutationObserver(() => {
      if (stopped) return;
      tryApply();
    });

    observer.observe(select, {
      attributes: true,
      childList: true,
      subtree: false,
    });

    tryApply();
  };

  const waitForLanguageSelect = (appBlock, shopLanguage) => {
    let stopped = false;
    let waitTimeoutId = null;
    let pollId = null;
    let lightObserver = null;
    let shadowObserver = null;

    const cleanup = () => {
      if (stopped) return;
      stopped = true;

      if (waitTimeoutId) window.clearTimeout(waitTimeoutId);
      if (pollId) window.clearInterval(pollId);
      if (lightObserver) lightObserver.disconnect();
      if (shadowObserver) shadowObserver.disconnect();
    };

    const onSelectFound = (select) => {
      cleanup();
      applyLanguageWithRetries(shopLanguage, select);
    };

    const check = () => {
      const select = findLanguageSelect(appBlock);
      if (select) {
        onSelectFound(select);
        return true;
      }
      return false;
    };

    const observeShadow = () => {
      const shadow = getClaimifyShadowRoot(appBlock);
      if (!shadow || shadowObserver) return;

      shadowObserver = new MutationObserver(() => {
        check();
      });

      shadowObserver.observe(shadow, {
        childList: true,
        subtree: true,
      });
    };

    if (check()) return;

    lightObserver = new MutationObserver(() => {
      observeShadow();
      check();
    });

    lightObserver.observe(appBlock, {
      childList: true,
      subtree: true,
    });

    observeShadow();

    pollId = window.setInterval(() => {
      observeShadow();
      check();
    }, POLL_MS);

    waitTimeoutId = window.setTimeout(cleanup, WAIT_FOR_SELECT_MS);
  };

  const watchClaimify = (appBlock, shopLanguage) => {
    if (WATCHED_BLOCKS.has(appBlock)) return;
    WATCHED_BLOCKS.add(appBlock);

    const claimifyScript = appBlock.querySelector(`script[src*="${CLAIMIFY_SCRIPT_MATCH}"]`);
    patchClaimifySettings(appBlock, shopLanguage);

    const startWhenReady = () => {
      waitForLanguageSelect(appBlock, shopLanguage);
    };

    if (!claimifyScript) {
      startWhenReady();
      return;
    }

    if (claimifyScript.dataset.appBlockLangBound === 'true') {
      startWhenReady();
      return;
    }

    claimifyScript.dataset.appBlockLangBound = 'true';

    claimifyScript.addEventListener(
      'load',
      () => {
        window.setTimeout(startWhenReady, 100);
      },
      { once: true }
    );
  };

  const initAppBlock = (appBlock) => {
    const shopLanguage = appBlock.dataset.shopLanguage || document.documentElement.lang || '';
    if (!shouldSyncLanguage(shopLanguage)) return;
    watchClaimify(appBlock, shopLanguage);
  };

  const init = () => {
    document.querySelectorAll('.app-block[data-shop-language]').forEach(initAppBlock);
  };

  if (document.querySelector('.app-block[data-shop-language]')) {
    init();
  } else if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
